import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { ESalesTextType, ESessionStore, ETabValue } from '../../data/Constants';
import { ConfigurationApi } from '../../api/ConfigurationApi';
import { SessionStore } from '../../services/SessionStore';
import {
  configured,
  loadedConfiguration,
  setProductImages,
  setPropertyAssociations,
  updateSubmodelDataInState,
} from '../Helpers/ConfigurationSliceHelper';
import { SalesTextApi } from '../../api/SalesTextApi';
import { PriceApi } from '../../api/PriceApi';
import { NotifyAPI } from '../../api/NotifyAPI';
import { ProductApi } from '../../api/ProductApi';
import {
  setAttributesToSectionsAndVariables,
  getProductORIProperty,
  updateProperties,
} from '../../services';
import { enableMapSet } from 'immer';

import type { AppState } from '../AppStore';
import type {
  IExtendedConfigureResponse,
  IImagesData,
  ISalesText,
  IMaterialPropertyAssociations,
  ISubModelData,
  IPriceData,
  ILoadConfiguration,
} from '../../../types';
import type {
  IClearConfigRequest,
  IAuthorizationRequest,
  IConfigurationAction,
  IConfigurationIdRequest,
  ICreateConfigurationRequest,
  ICurrencyRequest,
  ILoadConfigurationRequest,
  IPriceRequest,
  IPropertyAssociationRequest,
  ISalesTextRequest,
  IOptionalRequest,
  IModelRequest,
  ISubModelRequest,
  IConfigurationRequest,
} from '../../../types/IApiTypes';
import { isValidCurrency } from '../../services/Price';

enableMapSet();

const initialState: IExtendedConfigureResponse = {
  refresh: false,
  configurationId: null,
  languageCode: null,
  viewIds: [],
  //Configure
  data: null,
  assignments: [],
  assignmentsBefore: [],
  lastChange: null,
  savedConfiguration: null,
  conflict: null,
  isInvalid: null, //to check if configuration is invalid on first configure call
  acceptedChanges: null,
  longSalesText: new Map<string, Map<string, string>>(),
  shortSalesText: new Map<string, Map<string, string>>(),
  pmLanguages: null,
  authState: 'initial',
  contextData: null,
  price: new Map<string, Map<string, number>>(),
  currencyList: [],
  bundledFeatures: {},
  loaderMessage: null,
  productImages: new Map<string, Map<string, IImagesData>>(),
  access: '',
  checkLandingTab: { isTabChanged: false, tabValue: ETabValue.Product },
  orderingInstructions: new Map<string, string>(),
  propertyAssociations: new Map<string, Map<string, IMaterialPropertyAssociations>>(),
  isDataSet: true,
  propertyDefinition: [],
  subModelData: new Map<string, ISubModelData[]>(),
  isDataLoaded: false,
  search: {
    key: '',
    completed: false,
    details: {
      count: 0,
      matches: [],
    },
    highlightResults: false,
  },
};

// Thunk for deleting a configuration
// Thunk for deleting a configuration using ConfigurationApi
export const deleteConfiguration = createAsyncThunk(
  'configuration/deleteConfiguration',
  async (action: { configurationId: string }, thunkAPI) => {
    try {
      const response = await ConfigurationApi.deleteConfiguration(action);
      return { configurationId: action.configurationId, data: response };
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

//Gets the views available for configuration id
export const getViewList = createAsyncThunk(
  'configuration/getViewList',
  async (action: IConfigurationIdRequest) => {
    return ConfigurationApi.getViewList(action);
  }
);

//Loads the Configuration
export const loadConfiguration = createAsyncThunk(
  'configuration/loadConfiguration',
  async (action: ILoadConfigurationRequest, thunkAPI) => {
    thunkAPI.dispatch(loadConfig(action));
    return ConfigurationApi.loadConfiguration(action, thunkAPI);
  }
);

//Configures the assignments and returns the Configure response
export const configure = createAsyncThunk(
  'configuration/configure',
  async (action: IConfigurationRequest, thunkAPI) => {
    const state = thunkAPI.getState() as any;
    const showCode = state.userSettings?.showCode; // Get showCode from userSettings slice
    thunkAPI.dispatch(callConfigure(action));
    const response = await ConfigurationApi.configure(action);

    // Return both response and showCode
    return {
      ...response,
      showCode: showCode, // Include showCode in the response
    };
  }
);

//Returns the sales text based on apitype - short or long
export const getSalesText = createAsyncThunk<
  Awaited<ReturnType<typeof SalesTextApi.salesText>> | undefined,
  ISalesTextRequest,
  { state: AppState } // typed so getState().configuration is IExtendedConfigureResponse
>('configuration/getSalesText', async (action: ISalesTextRequest, thunkAPI) => {
  const state: IExtendedConfigureResponse = thunkAPI.getState().configuration;
  const shortSalesTextForModel = state?.shortSalesText?.get(action?.modelId);
  const longSalesTextForModel = state?.longSalesText?.get(action?.modelId);
  if (
    (action.apiType === ESalesTextType.Short &&
      (!shortSalesTextForModel || shortSalesTextForModel?.size <= 1)) ||
    (action.apiType === ESalesTextType.Long &&
      (!longSalesTextForModel || longSalesTextForModel?.size <= 1))
  ) {
    return SalesTextApi.salesText(action);
  }
  return undefined;
});

//Return the languages available for a product model
export const getPMLanguages = createAsyncThunk(
  'configuration/getPMLanguages',
  async (action: IConfigurationIdRequest) => {
    return ConfigurationApi.getPMLanguages(action);
  }
);

//Gets both sales text and price in parallel for a model
//Gets short sales text and/or price in parallel for a model
export const getShortSalesTextAndPrice = createAsyncThunk<
  {
    shortSalesText: Awaited<ReturnType<typeof SalesTextApi.salesText>> | null;
    price: Awaited<ReturnType<typeof PriceApi.price>> | null;
    modelId: string;
  },
  {
    salesTextShort?: Omit<ISalesTextRequest, 'modelId' | 'apiType'>;
    price?: Omit<IPriceRequest, 'modelId'>;
    modelId: string;
  },
  { state: AppState } // typed so getState().configuration is IExtendedConfigureResponse
>('configuration/getShortSalesTextAndPrice', async (action, thunkAPI) => {
  const state: IExtendedConfigureResponse = thunkAPI.getState().configuration;
  const promises: Promise<any>[] = [];
  const shortSalesTextForModel = state?.shortSalesText?.get(action?.modelId);
  // Add short sales text - only if not already loaded

  if (action.salesTextShort && (!shortSalesTextForModel || shortSalesTextForModel?.size <= 1)) {
    thunkAPI.dispatch(setInitSalesText({ modelId: action.modelId, apiType: ESalesTextType.Short }));
    promises.push(
      SalesTextApi.salesText({
        ...action.salesTextShort,
        modelId: action.modelId,
        apiType: ESalesTextType.Short,
      })
    );
  } else {
    promises.push(Promise.resolve(null));
  }

  const applicationSettingState = thunkAPI.getState() as any;

  // Add price - only if not already loaded

  if (
    action.price &&
    !state.price?.get(action.modelId) &&
    isValidCurrency(applicationSettingState.applicationSettings.currency.currencyCode)
  ) {
    thunkAPI.dispatch(setInitPrice({ modelId: action.modelId }));
    promises.push(PriceApi.price({ ...action.price, modelId: action.modelId }));
  } else {
    promises.push(Promise.resolve(null));
  }

  const results = await Promise.allSettled(promises);
  const shortSalesText = results[0].status === 'fulfilled' ? results[0].value : null;
  const price = results[1].status === 'fulfilled' ? results[1].value : null;

  return {
    shortSalesText,
    price,
    modelId: action.modelId,
  };
});

//Returns the prices of Product model
export const getPrice = createAsyncThunk<
  Awaited<ReturnType<typeof PriceApi.price>> | undefined,
  IPriceRequest,
  { state: AppState } // typed so getState().configuration is IExtendedConfigureResponse
>('configuration/getPrice', async (action: IPriceRequest, thunkAPI) => {
  const state: IExtendedConfigureResponse = thunkAPI.getState().configuration;
  if (!state.price?.get(action?.modelId)) {
    thunkAPI.dispatch(setInitPrice({ modelId: action.modelId }));
    return PriceApi.price(action);
  }
  return undefined;
});

//Returns the currencies available for the market given for configuration
export const getCurrencyList = createAsyncThunk(
  'configuration/getCurrencyList',
  async (action: ICurrencyRequest) => {
    return PriceApi.currency(action);
  }
);

//Clears the configuration
export const clearConfiguration = createAsyncThunk(
  'configuration/clearConfiguration',
  async (action: IClearConfigRequest) => {
    return ConfigurationApi.clearConfiguration(action);
  }
);

//Returns the price list map for the selected country/market for configuration
export const getPriceListMap = createAsyncThunk(
  'configuration/getPriceListMap',
  async (action: IAuthorizationRequest) => {
    return PriceApi.priceListMap(action);
  }
);

//Creates a configuration
export const createConfiguration = createAsyncThunk(
  'configuration/createConfiguration',
  async (action: ICreateConfigurationRequest) => {
    return ConfigurationApi.createConfiguration(action);
  }
);

//Notify which is save and close
export const notify = createAsyncThunk(
  'configuration/notify',
  async (action: IConfigurationIdRequest) => {
    return NotifyAPI.notify(action);
  }
);

//Gets all the available images for the product model
export const getProductImages = createAsyncThunk<
  Awaited<ReturnType<typeof ProductApi.getProductImages>> | undefined,
  IModelRequest,
  { state: AppState } // typed so getState().configuration is IExtendedConfigureResponse
>('configuration/getProductImages', async (action: IModelRequest, thunkAPI) => {
  const state: IExtendedConfigureResponse = thunkAPI.getState().configuration;
  if (!state.productImages?.get(action?.modelId)) {
    thunkAPI.dispatch(setInitProductImages({ modelId: action.modelId }));
    return ProductApi.getProductImages(action);
  }
  return undefined;
});

//sets the optional items to db
export const setOptionalItemsApi = createAsyncThunk(
  'configuration/setOptionalItems',
  async (action: IOptionalRequest) => {
    return ConfigurationApi.setOptionalItems(action);
  }
);

//gets the property associations
export const getPropertyAssociations = createAsyncThunk<
  Awaited<ReturnType<typeof ProductApi.getPropertyAssociations>> | undefined,
  IPropertyAssociationRequest,
  { state: AppState } // typed so getState().configuration is IExtendedConfigureResponse
>(
  'configuration/getPropertyAssociations',
  async (action: IPropertyAssociationRequest, thunkAPI) => {
    const state: IExtendedConfigureResponse = thunkAPI.getState().configuration;
    if (!state.propertyAssociations?.get(action?.productId)) {
      thunkAPI.dispatch(setInitPropertyAssociation({ modelId: action.productId }));
      return ProductApi.getPropertyAssociations(action, thunkAPI);
    }
    return undefined;
  }
);

//gets the submodel countvariable data
export const getSubModelData = createAsyncThunk(
  'configuration/getSubModelData',
  async (action: ISubModelRequest) => {
    return ProductApi.getSubModelData(action);
  }
);

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    loadConfig(state, action) {
      state.configurationId = action.payload.configurationId;
      state.languageCode = action.payload.languageCode;
      state.isInvalid = null;
      state.acceptedChanges = null;
    },
    setRefreshTrue(state) {
      state.refresh = true;
    },
    callConfigure(state, action) {
      state.assignments = action.payload.request?.configureRequest?.line.variableAssignments;
    },
    clearConflict(state) {
      state.conflict = null;
    },
    setAuthState(state, action) {
      state.authState = action.payload.authState;
    },
    resetModelPrice(state) {
      state.price = initialState.price;
    },
    clearOptionalItem(state) {
      if (state.savedConfiguration) {
        state.savedConfiguration.optionalItems = new Map<string, boolean>();
      }
    },

    setLoaderMessage(state, action) {
      state.loaderMessage = action.payload;
    },
    resetConfiguration() {
      return initialState;
    },
    updateSearchKey(state, action) {
      state.search.key = action.payload.searchKey;
    },
    setSearchFlagToSectionsVariables(state, action) {
      state.search.key = action.payload.searchKey;
      state.search.completed = false;
      state.search.highlightResults = action.payload.highlightResults;
      if (action.payload.highlightResults) {
        state.search.completed = true;
      }
    },
    setSearchValues(state, action) {
      state.search.details = action.payload.searchresult || initialState.search.details;
    },
    changeLandingTab(state, action) {
      state.checkLandingTab = {
        isTabChanged: action.payload.value,
        tabValue: action.payload.tabValue,
      };
    },
    setOptionalData(state, action) {
      if (state.savedConfiguration) {
        state.savedConfiguration.optionalItems = action.payload.optionalItems;
      }
    },
    setInitPropertyAssociation(state, action) {
      state.propertyAssociations?.set(
        action.payload.modelId,
        new Map<string, IMaterialPropertyAssociations>()
      );
    },
    setInitPrice(state, action) {
      state.price?.set(action.payload.modelId, new Map<string, number>());
    },
    setInitProductImages(state, action) {
      state.productImages?.set(action.payload.modelId, new Map<string, IImagesData>());
    },
    setInitSalesText(state, action) {
      if (action.payload.apiType === ESalesTextType.Long) {
        state.longSalesText?.set(action.payload.modelId, new Map<string, string>());
      } else if (action.payload.apiType === ESalesTextType.Short) {
        state.shortSalesText?.set(action.payload.modelId, new Map<string, string>());
      }
    },
    resetSalesText(state) {
      state.shortSalesText = initialState.shortSalesText;
      state.longSalesText = initialState.longSalesText;
    },
    updateSubmodelSalesText(state, action) {
      updateSubmodelDataInState(state, action.payload.language, false);
      state.data = setAttributesToSectionsAndVariables(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getViewList.fulfilled, (state, action) => {
        // handleError returns an object with 'error' — bail early if the API failed
        if ('error' in action.payload) return;
        if (action.payload.data) {
          SessionStore.set(
            ESessionStore.ViewId,
            action.payload.data.length > 0 && action.payload.data[0]
          );
          state.viewIds = action.payload.data;
        }
      })
      .addCase(loadConfiguration.fulfilled, (state, action) => {
        // handleError returns an object with 'error' — bail early if the API failed
        if ('error' in action.payload) return;
        if (action.payload.data) {
          const configAction: IConfigurationAction = {
            data: action.payload.data as ILoadConfiguration,
            access: action.payload.access ?? '',
          };
          const assigndData = loadedConfiguration(configAction, state);
          state.data = assigndData.data;
          state.assignments = assigndData.assignments;
          state.access = assigndData.access;
          state.contextData = assigndData.contextData;
          state.conflict = assigndData.conflict;
          state.isInvalid = assigndData.isInvalid;
          state.acceptedChanges = assigndData.acceptedChanges;
          state.savedConfiguration = assigndData.savedConfiguration;
          state.isDataSet = true;
          state.data = setAttributesToSectionsAndVariables(state);
        }
      })
      .addCase(getSalesText.fulfilled, (state, action) => {
        // If payload is null, data was already cached - skip processing
        if (!action.payload) {
          return;
        }
        // handleError returns an object with 'error' — bail early if the API failed
        if ('error' in action.payload) return;

        state.isDataSet = true;
        const salestext = {
          data: action.payload.salesText?.salesTexts ?? [],
          modelId: action.payload.modelId,
          apiType: action.payload.apiType,
        };
        const shortSalesTextForModel = state?.shortSalesText?.get(salestext.modelId);
        const longSalesTextForModel = state?.longSalesText?.get(salestext.modelId);
        // Store sales text in appropriate state map based on API type (short or long)
        if (salestext.apiType === ESalesTextType.Short) {
          if (shortSalesTextForModel && shortSalesTextForModel.size <= 1) {
            salestext.data.push({
              id: salestext.modelId,
              text: shortSalesTextForModel.get(salestext.modelId),
            });
          }
          state.shortSalesText?.set(
            salestext.modelId,
            new Map(salestext.data.map((item: ISalesText) => [item.id, item.text]))
          );
          state.data = setAttributesToSectionsAndVariables(state);
        } else if (salestext.apiType === ESalesTextType.Long) {
          if (longSalesTextForModel && longSalesTextForModel.size <= 1) {
            salestext.data.push({
              id: salestext.modelId,
              text: longSalesTextForModel.get(salestext.modelId),
            });
          }
          state.longSalesText?.set(
            salestext.modelId,
            new Map(salestext.data.map((item: ISalesText) => [item.id, item.text]))
          );
        }
      })
      .addCase(getSalesText.rejected, (state, action) => {
        const params = action.meta.arg;
        //reset the short salestext data to initial state if API failed
        if (params.modelId && params.apiType === ESalesTextType.Short) {
          state.shortSalesText?.set(params.modelId, new Map<string, string>());
        }

        //reset the short salestext data to initial state if API failed
        if (params.modelId && params.apiType === ESalesTextType.Long) {
          state.longSalesText?.set(params.modelId, new Map<string, string>());
        }
        state.data = setAttributesToSectionsAndVariables(state);
      })
      .addCase(getPMLanguages.fulfilled, (state, action) => {
        if (action.payload?.pmLanguages) {
          state.pmLanguages = action.payload?.pmLanguages[0].languages;
        }
      })
      .addCase(getPrice.fulfilled, (state, action) => {
        //This check is added for avoiding null error when already prices is  available for the modelId
        //Dispatch event return null when already data is available for requested model id
        if (action?.payload?.price) {
          const priceData = {
            prices: action.payload.price.prices ?? [],
            modelId: action.payload.modelId,
          };
          state.price.set(
            priceData.modelId,
            new Map(priceData.prices.map((item: IPriceData) => [item.materialCode, item.price]))
          );
          state.isDataSet = true;
          state.data = setAttributesToSectionsAndVariables(state);
        }
      })
      .addCase(getPrice.rejected, (state, action) => {
        const params = action.meta.arg;
        state.price?.set(params.modelId, new Map<string, number>());
        state.data = setAttributesToSectionsAndVariables(state);
      })
      .addCase(getCurrencyList.fulfilled, (state, action) => {
        if (action.payload?.currency) {
          state.currencyList = action.payload.currency;
        }
      })
      .addCase(getProductImages.fulfilled, (state, action) => {
        if (!action.payload || !('productImages' in action.payload)) return;
        const imageData = {
          productImages: action.payload.productImages.productImages ?? [],
          modelId: action.payload.modelId,
        };
        setProductImages(imageData, state);
      })
      .addCase(getProductImages.rejected, (state, action) => {
        const params = action.meta.arg;
        state.productImages?.set(params.modelId, new Map<string, IImagesData>());
      })
      .addCase(getPropertyAssociations.fulfilled, (state, action) => {
        if (action.payload?.propertiesAssociation) {
          const { modelId, propertyDefinition } = action.payload;

          //set property associations to the state
          setPropertyAssociations(action.payload, state);

          //assign property definitions to the state
          state.propertyDefinition =
            propertyDefinition?.length > 0 ? propertyDefinition : state.propertyDefinition;

          //set property associations to the variables if all mandatory properties are available
          if (state.data) {
            state.data = updateProperties(
              state.data,
              state.propertyAssociations,
              state.propertyDefinition
            );
          }

          //set ordering instructions for the product if available
          const oriProperty = getProductORIProperty(state.propertyAssociations, modelId);
          if (oriProperty) {
            state.orderingInstructions.set(modelId, oriProperty);
          }
        }
      })
      .addCase(getSubModelData.fulfilled, (state, action) => {
        state.subModelData?.set(action.payload.modelId, action.payload.subModelData.subModelData);
        updateSubmodelDataInState(state, action.meta.arg?.language, true);
      })
      .addCase(getSubModelData.rejected, (state, action) => {
        const params = action.meta.arg;
        state.subModelData?.set(params.productId, []);
      })
      .addCase(getShortSalesTextAndPrice.fulfilled, (state, action) => {
        if (action.payload) {
          state.isDataSet = true;
          const { shortSalesText, price, modelId } = action.payload;
          const shortSalesTextForModel = state?.shortSalesText?.get(modelId);

          // Handle short sales text
          if (shortSalesText && !('error' in shortSalesText) && shortSalesText.salesText) {
            state.shortSalesText?.set(
              modelId,
              new Map(
                shortSalesText.salesText.salesTexts.map((item: ISalesText) => [item.id, item.text])
              )
            );
          } else if (
            shortSalesText === null &&
            (!shortSalesTextForModel || shortSalesTextForModel?.size <= 1)
          ) {
            // Initialize with empty map if API was skipped or returned null
            state.shortSalesText?.set(modelId, new Map<string, string>());
          }

          // Handle price
          if (price?.price) {
            state.price.set(
              modelId,
              new Map(price.price.prices.map((item: IPriceData) => [item.materialCode, item.price]))
            );
          } else if (price === null && !state.price?.has(modelId)) {
            // Initialize with empty map if API was skipped or returned null
            state.price?.set(modelId, new Map<string, number>());
          }
          state.data = setAttributesToSectionsAndVariables(state);
        }
      })
      .addCase(getShortSalesTextAndPrice.rejected, (state, action) => {
        // Handle rejection - individual API failures are handled in Promise.allSettled
        const modelId = action.meta.arg.modelId;
        if (modelId) {
          // Reset to empty maps to indicate API call completed (even if failed)
          if (!state.shortSalesText?.has(modelId)) {
            state.shortSalesText?.set(modelId, new Map<string, string>());
          }
          if (!state.price?.has(modelId)) {
            state.price?.set(modelId, new Map<string, number>());
          }
          state.data = setAttributesToSectionsAndVariables(state);
        }
      })
      .addMatcher(isAnyOf(configure.fulfilled, clearConfiguration.fulfilled), (state, action) => {
        // handleError returns an object with 'error' — bail early if the API failed
        if ('error' in action.payload) return;
        if (action.payload.configuration) {
          state.isDataSet = true;
          const assigndData = configured(action.payload as IConfigurationAction, state);
          state.conflict = assigndData?.conflict;
          state.data = assigndData?.data;
          state.assignments = assigndData?.assignments;
          state.assignmentsBefore = assigndData.assignmentsBefore;
          state.isInvalid =
            assigndData?.isInvalid === false || assigndData?.isInvalid
              ? assigndData.isInvalid
              : null;
          state.acceptedChanges = assigndData?.acceptedChanges;
          state.contextData = assigndData?.contextData;
          state.data = setAttributesToSectionsAndVariables(state);
          state.lastChange =
            'lastChange' in action.payload ? (action.payload.lastChange ?? null) : null;
        }
      });
  },
});

export const {
  loadConfig,
  callConfigure,
  clearConflict,
  setAuthState,
  resetModelPrice,
  clearOptionalItem,
  setLoaderMessage,
  resetConfiguration,
  setSearchFlagToSectionsVariables,
  updateSearchKey,
  changeLandingTab,
  setOptionalData,
  setInitPrice,
  setInitProductImages,
  setInitSalesText,
  resetSalesText,
  setInitPropertyAssociation,
  setRefreshTrue,
  setSearchValues,
  updateSubmodelSalesText,
} = configurationSlice.actions;

export default configurationSlice.reducer;
